import { Component, EventEmitter, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CONFORMANCE_RULE_TYPE,
    CreateFlightConformanceRuleCommand,
    DO_NOTHING,
    FEATURE_LOTAM,
    FlightConformancePoliciesService,
    FlightConformancePolicyDto,
    FlightConformanceRuleDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    hasFeatureFlag,
    UpdateFlightConformanceRuleCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FormatFlightConformanceTypePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { EnhancedHelpService } from 'libs/enhanced-help/src/lib/enhanced-help.service';
import { firstValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

const editRuleFields: FormlyFieldConfig[] = [
    {
        key: 'description',
        type: 'textarea',
        props: {
            label: 'Description',
            description:
                'A description of what this rule is enforcing so the RP can understand the reason for the alert',
            required: true
        }
    },
    {
        key: 'defaultValue',
        type: 'input',
        props: {
            label: 'Default Value',
            description:
                'The value that is used if another value can not be derived from the mission or related configuration',
            type: 'number',
            min: 0
        },
        hideExpression:
            'model.type === "FLIGHT_AREA" || model.type === "NOTICES"'
    },
    {
        key: 'maximumValue',
        type: 'input',
        props: {
            label: 'Maximum Value',
            description:
                'The hard maximum value to be used, even if the mission or related configuration has a different value',
            type: 'number',
            min: 0
        },
        hideExpression:
            'model.type === "FLIGHT_AREA" || model.type === "LANDING_BATTERY_LEVEL" || model.type === "NOTICES"'
    }
];

@Component({
    selector: 'flight-conformance-policy',
    template: ` <div class="horizontal-container pad-children center-children">
            <mat-slide-toggle
                tooltip
                placement="top"
                appendTo="body"
                enhancedHelp="notify-enabled"
                [disabled]="working || isReadOnlyAdmin"
                [checked]="policy.enabled"
                (change)="
                    updatePolicyActions(
                        policy,
                        $event.checked,
                        policy.notifyRp,
                        policy.notifyRpic,
                        policy.notifyApprover
                    )
                "
            >
                Enabled
            </mat-slide-toggle>

            <mat-slide-toggle
                tooltip
                placement="top"
                appendTo="body"
                enhancedHelp="notify-rp"
                [disabled]="working || isReadOnlyAdmin"
                [checked]="policy.notifyRp"
                (change)="
                    updatePolicyActions(
                        policy,
                        policy.enabled,
                        $event.checked,
                        policy.notifyRpic,
                        policy.notifyApprover
                    )
                "
            >
                Notify RP
            </mat-slide-toggle>

            <mat-slide-toggle
                tooltip
                placement="top"
                appendTo="body"
                enhancedHelp="notify-rpic"
                [disabled]="working || isReadOnlyAdmin"
                [checked]="policy.notifyRpic"
                (change)="
                    updatePolicyActions(
                        policy,
                        policy.enabled,
                        policy.notifyRp,
                        $event.checked,
                        policy.notifyApprover
                    )
                "
            >
                Notify RPIC
            </mat-slide-toggle>

            <mat-slide-toggle
                tooltip
                placement="top"
                appendTo="body"
                enhancedHelp="notify-approver"
                [disabled]="working || isReadOnlyAdmin"
                [checked]="policy.notifyApprover"
                (change)="
                    updatePolicyActions(
                        policy,
                        policy.enabled,
                        policy.notifyRp,
                        policy.notifyRpic,
                        $event.checked
                    )
                "
            >
                Notify Approver
            </mat-slide-toggle>
            <button
                tooltip
                placement="top"
                appendTo="body"
                enhancedHelp="policy-reprocess"
                type="button"
                (click)="reprocess()"
                [disabled]="working || isReadOnlyAdmin"
                class="btn btn-sm"
            >
                Reprocess
            </button>
        </div>

        <div class="horizontal-container">
            <button
                type="button"
                tooltip
                placement="top"
                appendTo="body"
                enhancedHelp="rule-add"
                class="btn-circle btn-link"
                (click)="newRule(policy)"
                [disabled]="working || isReadOnlyAdmin"
            >
                <span class="fa fa-plus"></span>
            </button>
            <div class="fill">
                <table class="table">
                    <thead>
                        <tr>
                            <th>Check Type</th>
                            <th>Enabled</th>
                            <th>Settings</th>
                            <th>Message</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let rule of policy.ruleList">
                            <td>
                                {{ rule.type | formatFlightConformanceType }}
                            </td>
                            <td>
                                <mat-slide-toggle
                                    [disabled]="working || isReadOnlyAdmin"
                                    [checked]="rule.enabled"
                                    (change)="
                                        updateRuleActions(
                                            policy,
                                            rule,
                                            $event.checked
                                        )
                                    "
                                >
                                </mat-slide-toggle>
                            </td>
                            <td>{{ rule | formatConformanceRuleSettings }}</td>
                            <td>{{ rule.description }}</td>
                            <td>
                                <button
                                    *ngIf="!isReadOnlyAdmin"
                                    class="btn btn-sm btn-tertiary"
                                    (click)="editRule(policy, rule)"
                                    [disabled]="working"
                                    tooltip="Edit"
                                >
                                    <span class="fal fa-pencil-alt"></span>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>`
})
export class FlightConformancePolicy {
    @Input()
    policy: FlightConformancePolicyDto;

    @Input() isReadOnlyAdmin: boolean;

    @Output()
    updatePolicy = new EventEmitter<FlightConformancePolicyDto>();

    @Output()
    updateRule = new EventEmitter<[number, FlightConformanceRuleDto]>();

    @Output()
    reprocessPolicy = new EventEmitter<void>();

    public working = false;

    private ngUnsubscribe$ = new Subject<void>();

    private selectRuleFields?: FormlyFieldConfig[];

    constructor(
        private workTracker: WorkTracker,
        private commonDialoguesService: CommonDialoguesService,
        private enhancedHelpService: EnhancedHelpService,
        private formatFlightConformanceType: FormatFlightConformanceTypePipe,
        private logging: FlyFreelyLoggingService,
        private flightConformancePoliciesService: FlightConformancePoliciesService,
        workspaceStateService: WorkspaceStateService
    ) {
        this.workTracker.observable
            .pipe(takeUntilDestroyed())
            .subscribe(working => (this.working = working));

        workspaceStateService.currentLoadedOrganisation$
            .pipe(take(1), takeUntilDestroyed())
            .subscribe({
                next: organisation => {
                    const hasConformance = hasFeatureFlag(
                        organisation.organisation,
                        FEATURE_LOTAM
                    );

                    this.selectRuleFields = [
                        {
                            key: 'type',
                            type: 'ng-select',
                            props: {
                                label: 'Rule Type',
                                required: true,
                                labelProp: 'name',
                                valueProp: 'value',
                                options: CONFORMANCE_RULE_TYPE.filter(
                                    t => hasConformance || t.value !== 'NOTICES'
                                )
                            }
                        }
                    ];
                },
                error: error =>
                    this.logging.error(
                        'Error while loading the current orgaisation',
                        error
                    )
            })
            .add(this.workTracker.createTracker());
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    newRule(policy: FlightConformancePolicyDto) {
        this.enhancedHelpService
            .findByScreen('flight-conformance-policy')
            .pipe(takeUntil(this.ngUnsubscribe$), take(1))
            .subscribe({
                next: help => {
                    const addRuleFields = this.selectRuleFields!.concat({
                        type: 'help',
                        props: {
                            lookupProp: 'type',
                            messageLookup: help.reduce(
                                (acc, v) => ({
                                    ...acc,
                                    [v.component]: v.helpText
                                }),
                                {}
                            )
                        }
                    }).concat(editRuleFields);

                    this.commonDialoguesService
                        .showFormlyDialogue(
                            'New Rule',
                            'Add',
                            true,
                            true,
                            addRuleFields,
                            {} as CreateFlightConformanceRuleCommand,
                            data =>
                                firstValueFrom(
                                    this.flightConformancePoliciesService
                                        .createRule(policy.id, data)
                                        .pipe(takeUntil(this.ngUnsubscribe$))
                                ),
                            'modal-task',
                            'flight-conformance-policy'
                        )
                        .then(
                            newRule => policy.ruleList.push(newRule),
                            DO_NOTHING
                        );
                },
                error: DO_NOTHING
            });
    }

    editRule(
        policy: FlightConformancePolicyDto,
        rule: FlightConformanceRuleDto
    ) {
        this.enhancedHelpService
            .findByScreenAndComponent(
                'flight-conformance-policy',
                rule.type,
                true
            )
            .pipe(takeUntil(this.ngUnsubscribe$), take(1))
            .subscribe({
                next: help => {
                    const fields = [
                        {
                            type: 'help',
                            props: {
                                message: help.helpText
                            }
                        } as FormlyFieldConfig
                    ].concat(editRuleFields);

                    this.commonDialoguesService
                        .showFormlyDialogue(
                            `Edit Rule - ${this.formatFlightConformanceType.transform(
                                rule.type
                            )}`,
                            'Save',
                            true,
                            true,
                            fields,
                            {
                                description: rule.description,
                                defaultValue: rule.defaultValue,
                                maximumValue: rule.maximumValue,
                                type: rule.type
                            } as UpdateFlightConformanceRuleCommand,
                            data =>
                                firstValueFrom(
                                    this.flightConformancePoliciesService
                                        .updateRule(
                                            policy.id,
                                            rule.ruleId,
                                            data
                                        )
                                        .pipe(takeUntil(this.ngUnsubscribe$))
                                ),
                            'modal-task',
                            'flight-conformance-policy'
                        )
                        .then(
                            newRule =>
                                (policy.ruleList = policy.ruleList.map(r =>
                                    r.ruleId === newRule.ruleId ? newRule : r
                                )),
                            DO_NOTHING
                        );
                },
                error: DO_NOTHING
            });
    }

    updatePolicyActions(
        policy: FlightConformancePolicyDto,
        enabled: boolean,
        notifyRp: boolean,
        notifyRpic: boolean,
        notifyApprover: boolean
    ) {
        this.flightConformancePoliciesService
            .update(policy.id, {
                enabled,
                notifyRp,
                notifyRpic,
                notifyApprover
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: saved => this.updatePolicy.emit(saved),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating policy: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    updateRuleActions(
        policy: FlightConformancePolicyDto,
        rule: FlightConformanceRuleDto,
        enabled: boolean
    ) {
        this.flightConformancePoliciesService
            .updateRuleActions(policy.id, rule.ruleId, {
                enabled
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: saved => this.updateRule.emit([policy.id, saved]),
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while updating rule: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    reprocess() {
        this.reprocessPolicy.next();
    }
}
